import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  useRunReportQuery,
  useGetReportSpecificFieldsQuery,
} from "../../../api/network/reportApiServices";
import { TModalsState } from "../../../api/store/commonSlice";
import svgIcons from "../../../assets/svg/svgIcons";
import AdminTable from "../../../components/adminTable";
import {
  APP_CONFIG,
  ASSET_CATEGORY,
  ASSET_CATEGORY_VALUES,
} from "../../../constants/constants";
import { jsTimeToDBTime } from "../../../utils/dateTime";
import ChartWrapper from "../components/chartWrapper";
import ModalSwitchChartType from "../components/modalSwitchChartType";
import { IWidgetProps } from "../components/overviewDragDropColumnItem";
import PillSelector from "../components/pillSelector";
import WidgetChartAreaGenerator, {
  widgetBarColorLightBlue,
} from "../components/widgetChartAreaGenerator";
import WidgetFilterSelector from "../components/widgetFilterSelector";
import { useOverviewContext } from "../context/overview.context";
import ModalWidgetDetails from "../modalWidgetDetails";
import { EWidgetChartTypes } from "../types/overview.types";
import { verticalLabelFormatter } from "../utils/chart";
import { useGroupsAndAssetsUtils } from "../hooks/overviewScreenUtils";

const tableMinHeightClass = "min-h-[100px]";
const tableMaxHeightClass = "max-h-[224px]";

const defaultChartType = EWidgetChartTypes.LINE_GRAPH;

const chartColors = ["#CB904D", "#51A3A3"];

interface IWidgetFuelEfficiency extends IWidgetProps {
  showDetails: boolean;
  setShowDetails: (value: boolean) => void;
}

const WidgetFuelEnergyConsumption: FC<IWidgetFuelEfficiency> = ({
  widgetType,
  showDetails,
  setShowDetails,
}) => {
  const { t: tOverview } = useTranslation("translation", {
    keyPrefix: "overviewScreen",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: `overviewScreen.widgets.enum_${widgetType}`,
  });

  const pills = ["Asset", "Driver"];

  const reportId = 292;

  const reportFieldIds = {
    fuel_usage_week1: 12783,
    fuel_usage_week2: 12784,
    fuel_usage_week3: 12785,
    fuel_usage_week4: 12786,
    fuel_usage_week5: 12787,
    electric_usage_week1: 12788,
    electric_usage_week2: 12789,
    electric_usage_week3: 12790,
    electric_usage_week4: 12791,
    electric_usage_week5: 12792,
  };

  const orderingFieldId = 12781;

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const [selectedChartType, setSelectedChartType] = useState(defaultChartType);

  const {
    allTimeFilters,
    timeFilter,
    allGroupsSelected,
    selectedGroups,
    selectedDataType,
    setSelectedDataType,
    allAssetsSelected,
    selectedAssets,
    setAllAssetsSelected,
    setSelectedAssets,
  } = useOverviewContext();

  const [filterCategory, setFilterCategory] = useState<ASSET_CATEGORY_VALUES>(
    ASSET_CATEGORY_VALUES.ALL
  );

  const {
    data: dataSpecificReport,
    isFetching: isFetchingSpecificReport,
    isUninitialized: isUninitializedSpecificReport,
  } = useGetReportSpecificFieldsQuery(
    {
      // type: REPORT_TYPES.CUSTOM,
      report_id: reportId,
    },
    { refetchOnMountOrArgChange: true }
  );
  const graphXAxisDataKey = "week";
  // filterCategory === ASSET_CATEGORY_VALUES.ICE ? "1" : "0";
  const graphYAxisDataKey = "fuel_usage";
  // filterCategory === ASSET_CATEGORY_VALUES.ELECTRIC ? "2" : "1";
  const graphRightYAxisDataKey = "electric_usage";
  // filterCategory === ASSET_CATEGORY_VALUES.HYBRID ? "3" : "2";

  const ordering = `${orderingFieldId}`; // no minus sign needed as we always want to show in ascending order of time (Jan, Feb, Mar...)

  const {
    reportData: dataReport,
    isFetching,
    isError,
    reportError,
  } = useGroupsAndAssetsUtils(
    selectedDataType,
    reportId,
    selectedAssets,
    1,
    10,
    ordering,
    {
      asset_category:
        filterCategory === ASSET_CATEGORY_VALUES.ALL
          ? undefined
          : ASSET_CATEGORY.find((item) => item.value === filterCategory)?.id,
    }
  );

  const MemoizedWidgetFilterSelector = // useCallback(() => {
    ({ limitWidth = true }: { limitWidth?: boolean }) => {
      return (
        <div className="mb-4 flex gap-2 flex-row-reverse Xgroup-[]:flex-row justify-between">
          <WidgetFilterSelector
            limitWidth={limitWidth}
            selectedFilterLabel={
              ASSET_CATEGORY.find((item) => item.value === filterCategory)
                ?.label
            }
            filters={ASSET_CATEGORY}
            setFilter={(newValue) => {
              console.log(newValue);
              setFilterCategory(newValue as ASSET_CATEGORY_VALUES);
            }}
          />
        </div>
      );
    };

  const roundedValue = useCallback((value: number) => {
    return Number(value.toFixed(2).replace(".00", ""));
  }, []);

  // , [selectedPill, sortOrder]);
  const [computedChartData, setComputedChartData] = useState<any[]>([]);
  useEffect(() => {
    if (
      !!dataReport &&
      !!dataReport?.results &&
      !!dataSpecificReport?.report_fields &&
      !!dataReport?.results?.[0]
    ) {
      let newComputedChartData = [] as any[];
      dataReport?.results?.forEach((item, monthIndex) => {
        newComputedChartData.push({
          [graphXAxisDataKey]: `Week ${monthIndex * 5 + 1}`,
          [graphYAxisDataKey]: roundedValue(
            item[reportFieldIds.fuel_usage_week1] || 0
          ),
          [graphRightYAxisDataKey]: roundedValue(
            item[reportFieldIds.electric_usage_week1] || 0
          ),
        });
        newComputedChartData.push({
          [graphXAxisDataKey]: `Week ${monthIndex * 5 + 2}`,
          [graphYAxisDataKey]: roundedValue(
            item[reportFieldIds.fuel_usage_week2] || 0
          ),
          [graphRightYAxisDataKey]: roundedValue(
            item[reportFieldIds.electric_usage_week2] || 0
          ),
        });
        newComputedChartData.push({
          [graphXAxisDataKey]: `Week ${monthIndex * 5 + 3}`,
          [graphYAxisDataKey]: roundedValue(
            item[reportFieldIds.fuel_usage_week3] || 0
          ),
          [graphRightYAxisDataKey]: roundedValue(
            item[reportFieldIds.electric_usage_week3] || 0
          ),
        });
        newComputedChartData.push({
          [graphXAxisDataKey]: `Week ${monthIndex * 5 + 4}`,
          [graphYAxisDataKey]: roundedValue(
            item[reportFieldIds.fuel_usage_week4] || 0
          ),
          [graphRightYAxisDataKey]: roundedValue(
            item[reportFieldIds.electric_usage_week4] || 0
          ),
        });
        newComputedChartData.push({
          [graphXAxisDataKey]: `Week ${monthIndex * 5 + 5}`,
          [graphYAxisDataKey]: roundedValue(
            item[reportFieldIds.fuel_usage_week5] || 0
          ),
          [graphRightYAxisDataKey]: roundedValue(
            item[reportFieldIds.electric_usage_week5] || 0
          ),
        });
      });
      setComputedChartData(newComputedChartData);
    }
  }, [dataReport, dataSpecificReport]);

  return (
    <div className="mt-2">
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre className="absolute text-[8px] text-field-error-dark -mt-2">
          reportId: {reportId}
        </pre>
      )}

      {/* Filter */}
      <MemoizedWidgetFilterSelector />

      {(!!dataReport && dataReport?.results?.length > 0) || isFetching ? (
        <>
          {isFetching ? (
            <ChartWrapper heightClass="pt-[45%]">
              <img
                src={svgIcons.LoadingAnimation}
                alt="laoding"
                className="absolute size-8 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              />
            </ChartWrapper>
          ) : (
            <>
              <WidgetChartAreaGenerator
                barChartMargin={
                  selectedChartType === EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                    ? { top: 0, right: 10, bottom: 0, left: -20 }
                    : {
                        top: 0,
                        right:
                          filterCategory === ASSET_CATEGORY_VALUES.ICE
                            ? 32
                            : -32,
                        bottom: 0,
                        left:
                          filterCategory === ASSET_CATEGORY_VALUES.ELECTRIC
                            ? 30
                            : -30,
                      }
                }
                barColor={widgetBarColorLightBlue}
                cellItemsForSectionColor={
                  [EWidgetChartTypes.PIE, EWidgetChartTypes.DOUGHNUT].includes(
                    selectedChartType
                  )
                    ? [...(dataReport?.results || [])]?.sort(
                        (a, b) => b[graphYAxisDataKey] - a[graphYAxisDataKey]
                      )
                    : undefined
                }
                chartColors={chartColors}
                chartType={selectedChartType}
                chartWrapperHeightClass={
                  selectedChartType === EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                    ? "pt-[100%]"
                    : "pt-[45%]"
                }
                chartHighlightValue={
                  !!dataReport && !!dataReport?.results
                    ? computedChartData?.find(
                        (item) => item[graphXAxisDataKey] === "total_count"
                      )?.value
                    : 0
                }
                computedChartData={
                  !!dataReport &&
                  !!dataReport?.results &&
                  !!dataSpecificReport?.report_fields
                    ? computedChartData.filter(
                        (item) => item[graphXAxisDataKey] !== "total_count"
                      )
                    : []
                }
                graphXAxisDataKey={graphXAxisDataKey}
                graphYAxisDataKey={graphYAxisDataKey}
                {
                  // filterCategory === ASSET_CATEGORY_VALUES.ALL ||
                  // filterCategory === ASSET_CATEGORY_VALUES.HYBRID
                  ...(true
                    ? {
                        graphRightYAxisDataKey,
                      }
                    : {})
                }
                {...(filterCategory === ASSET_CATEGORY_VALUES.ICE ||
                filterCategory === ASSET_CATEGORY_VALUES.ELECTRIC
                  ? {
                      hideYAxis:
                        filterCategory === ASSET_CATEGORY_VALUES.ICE
                          ? "right"
                          : "left",
                    }
                  : {})}
                legendFormatter={(value, entry, index) => (
                  <span
                    key={`item-${index}`}
                    style={{
                      color: entry.color,
                      fontSize: "10px",
                    }}
                  >
                    {value === graphYAxisDataKey ? "ICE (gallons)" : "EV (kWh)"}
                  </span>
                )}
                legendVerticalAlignClass="justify-start"
                showAverageReferenceLineWithLabel={tOverview(
                  "charts.average_label"
                )}
                showChartTypeSwitchButton={false}
                t={t}
                tooltipFormatter={(value: any, name: any) => {
                  return [
                    `${name === graphYAxisDataKey ? "ICE (gallons)" : "EV (kWh)"}: ${!!value ? verticalLabelFormatter(value) : 0}`,
                  ];
                }}
                widgetType={widgetType}
                xAxisTickLabelWidth={24}
                xAxisTickUsesTranslation={false}
                yAxisTickFormatter={(value) =>
                  value === 0 ? "0" : verticalLabelFormatter(value)
                }
              />
              {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                <pre className="text-[8px] leading-[8px]">
                  {JSON.stringify(computedChartData, null, 2)}
                </pre>
              )}
            </>
          )}

          {/* Table */}
          {isFetching && (
            <div
              className={`${tableMinHeightClass} ${tableMaxHeightClass} flex justify-center items-center relative`}
            >
              <img
                src={svgIcons.LoadingAnimation}
                alt="laoding"
                className="absolute size-8 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              />
            </div>
          )}
        </>
      ) : (
        <p className="p-6 text-center text-sm text-heading-gray888">
          {tOverview("no_widget_data")}
        </p>
      )}
      {modalsState.showSwitchChartType &&
        modalsState.switchChartTypeForWidget === widgetType && (
          <ModalSwitchChartType
            selectedChartType={selectedChartType}
            onChartTypeChange={(newChartType) => {
              setSelectedChartType(newChartType);
            }}
          />
        )}
    </div>
  );
};
export default WidgetFuelEnergyConsumption;
